import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { DropdownSelect, Modal, ModalBody, ModalFoot, ModalHead, SelectItem, Text, } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { conversationsApi, } from 'apis/conversations';
import { analytics } from 'app/utils/segment';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { ConversationsBulkModalCancelCsatAction } from './components/actions/cancelCsat/conversationsBulkModalCancelCsatAction';
import { ConversationsBulkModalCloseAction } from './components/actions/close/conversationsBulkModalCloseAction';
import { ConversationsBulkModalInternalNoteAction } from './components/actions/internalNote/conversationsBulkModalInternalNoteAction';
import { ConversationsBulkModalSendReplyAction } from './components/actions/sendReply/conversationsBulkModalSendReplyAction';
import { ConversationsBulkModalTagAction } from './components/actions/tag/conversationsBulkModalTagAction';
import { ConversationsBulkModalTransferToQueueAction } from './components/actions/transferToQueue/conversationsBulkModalTransferToQueueAction';
import { conversationsBulkModalActions } from './conversationsBulkModal.actions';
import styles from './conversationsBulkModal.module.scss';
import { conversationsBulkModalActionLabel } from './utils/conversationsBulkModalActionLabel';
import { getFailedValidationErrorMessage } from './utils/getFailedValidationErrorMessage';
const bulkActionSegmentNameMap = {
    add_internal_note: 'addInternalNote',
    add_tag: 'addTag',
    cancel_csat: 'cancelCSAT',
    claim: ' claim',
    close: 'close',
    remove_tag: 'removeTag',
    send_reply: 'sendReply',
    transfer_to_queue: 'transferToQueue',
};
const bulkActionValidationNameMap = {
    SEND_REPLY: 'send_reply',
};
/** Represents the modal for performing bulk actions on conversations. */
export const ConversationsBulkModal = createAppModal({ type: 'app:conversationsBulk', featureFlag: 'has-bulk-operations' }, ({ isOpen, modal, meta, hide }) => {
    const dispatch = useAppDispatch();
    const conversationIds = useMemo(() => (modal === null || modal === void 0 ? void 0 : modal.arg.conversationIds) || [], [modal === null || modal === void 0 ? void 0 : modal.arg.conversationIds]);
    const [readConversationsValidationBulk, { data: readConversationsValidationBulkData, isFetching, isSuccess },] = conversationsApi.useLazyReadConversationsValidationBulkQuery();
    // Handles getting validation information.
    useEffect(() => {
        if (isOpen) {
            readConversationsValidationBulk({ csids: conversationIds });
        }
    }, [conversationIds, isOpen, readConversationsValidationBulk]);
    // Keeps track of selected actions.
    const [selectedActions, setSelectedActions] = useState([]);
    // Keeps track of selected action types.
    const [selectedActionTypes, setSelectedActionTypes] = useState([]);
    const legacyBulkUpdateAsyncStatus = useAppSelector((state) => state.bulkActions.meta.asyncStatus);
    const canPerformBulkUpdate = useMemo(() => selectedActions.length > 0 &&
        selectedActions.every((selectedAction) => selectedAction.isValid) &&
        legacyBulkUpdateAsyncStatus !== 'loading', [legacyBulkUpdateAsyncStatus, selectedActions]);
    /** Hides and resets the modal to initial state. */
    const handleResetAndHide = () => {
        setSelectedActionTypes([]);
        setSelectedActions([]);
        hide === null || hide === void 0 ? void 0 : hide();
    };
    /** Handles confirming the modal and performing the bulk actions. */
    const handleConfirm = () => {
        if (meta) {
            analytics.bulkActionsUpdateClicked({
                selectedActions: selectedActions.map(({ type }) => bulkActionSegmentNameMap[type]),
                selectedActionsCount: selectedActions.length,
                conversationsCount: conversationIds.length,
                location: modal.arg.location,
                modalVersion: '2',
            });
            dispatch(conversationsBulkModalActions.initBulkUpdate({
                actions: selectedActions,
            }));
            meta.resolve({ isConfirmed: true, isCancelled: false });
            handleResetAndHide();
        }
    };
    /** Handles cancelling the modal. */
    const handleCancel = () => {
        if (meta) {
            handleResetAndHide();
            meta.resolve({ isConfirmed: false, isCancelled: true });
        }
    };
    /** Handles selection of an action. */
    const handleActionSelect = useCallback(({ id: actionType }) => {
        const type = actionType;
        if (!selectedActionTypes.includes(type)) {
            setSelectedActionTypes((previousSelectedActionTypes) => [
                ...previousSelectedActionTypes,
                type,
            ]);
        }
    }, [selectedActionTypes]);
    /** The rendered dropdown selectable actions. */
    const dropdownSelectActions = useMemo(() => Object.keys(conversationsBulkModalActionLabel)
        .filter((bulkAction) => !selectedActionTypes.includes(bulkAction))
        .map((unselectedAction) => ({
        id: unselectedAction,
        name: conversationsBulkModalActionLabel[unselectedAction],
    })), [selectedActionTypes]);
    /** The ids of the disabled actions. */
    const disabledActionIds = useMemo(() => {
        var _a;
        return ((_a = readConversationsValidationBulkData === null || readConversationsValidationBulkData === void 0 ? void 0 : readConversationsValidationBulkData.data.validations_failed) !== null && _a !== void 0 ? _a : []).map(({ validation_type }) => bulkActionValidationNameMap[validation_type]);
    }, [readConversationsValidationBulkData]);
    /** The dropdown tooltip items. */
    const dropdownSelectTooltipItems = useMemo(() => {
        if (!(readConversationsValidationBulkData === null || readConversationsValidationBulkData === void 0 ? void 0 : readConversationsValidationBulkData.data.validations_failed)) {
            return {};
        }
        const validationErrorMessages = readConversationsValidationBulkData.data.validations_failed.reduce((failedValidationMessages, { csids, error, validation_type }) => {
            const bulkActionType = bulkActionValidationNameMap[validation_type];
            return Object.assign(Object.assign({}, failedValidationMessages), { [bulkActionType]: [
                    failedValidationMessages[bulkActionType],
                    getFailedValidationErrorMessage({ csids, error }),
                ].filter(Boolean) });
        }, {});
        return Object.keys(validationErrorMessages).reduce((tooltipItems, bulkActionType) => (Object.assign(Object.assign({}, tooltipItems), { [bulkActionType]: {
                content: (_jsxs(_Fragment, { children: [_jsxs("strong", { children: [conversationsBulkModalActionLabel[bulkActionType], " action is disabled"] }), _jsx("ul", Object.assign({ className: styles.tooltipList }, { children: validationErrorMessages[bulkActionType].map((message) => (_jsx("li", { children: message }, message))) }))] })),
                dark: true,
            } })), {});
    }, [readConversationsValidationBulkData]);
    /**
     * Handles the change of an action.
     *
     * @param action The bulk action object.
     */
    const handleActionChange = useCallback((action) => setSelectedActions((previousSelectedActions) => previousSelectedActions.find((selectedAction) => selectedAction.type === action.type)
        ? previousSelectedActions.map((previousSelectedAction) => previousSelectedAction.type === action.type
            ? action
            : previousSelectedAction)
        : [...previousSelectedActions, action]), []);
    /**
     * Removes action from currently selected action types.
     *
     * @param type The action type to remove.
     */
    const handleActionRemove = useCallback((type) => {
        setSelectedActionTypes((previousSelectedActionTypes) => previousSelectedActionTypes.filter((previousSelectedActionType) => previousSelectedActionType !== type));
        setSelectedActions((previousSelectedActions) => previousSelectedActions.filter((previousSelectedAction) => previousSelectedAction.type !== type));
        // TODO: [TEAMS-8097] Add tracking events
    }, []);
    /** The map of actions elements. */
    const actionElements = useMemo(() => ({
        add_internal_note: (_jsx(ConversationsBulkModalInternalNoteAction, { conversationIds: conversationIds, onRemove: handleActionRemove, onChange: handleActionChange }, "add_internal_note")),
        add_tag: (_jsx(ConversationsBulkModalTagAction, { type: "add_tag", conversationIds: conversationIds, onRemove: handleActionRemove, onChange: handleActionChange }, "add_tag")),
        cancel_csat: (_jsx(ConversationsBulkModalCancelCsatAction, { conversationIds: conversationIds, onRemove: handleActionRemove, onChange: handleActionChange }, "cancel_csat")),
        close: (_jsx(ConversationsBulkModalCloseAction, { conversationIds: conversationIds, onRemove: handleActionRemove, onChange: handleActionChange }, "close")),
        remove_tag: (_jsx(ConversationsBulkModalTagAction, { type: "remove_tag", conversationIds: conversationIds, onRemove: handleActionRemove, onChange: handleActionChange }, "remove_tag")),
        send_reply: (_jsx(ConversationsBulkModalSendReplyAction, { conversationIds: conversationIds, onRemove: handleActionRemove, onChange: handleActionChange }, "send_reply")),
        transfer_to_queue: (_jsx(ConversationsBulkModalTransferToQueueAction, { conversationIds: conversationIds, onRemove: handleActionRemove, onChange: handleActionChange }, "transfer_to_queue")),
    }), [conversationIds, handleActionChange, handleActionRemove]);
    /** The rendered selected action elements. */
    const selectedActionElements = useMemo(() => selectedActionTypes.map((selectedAction) => actionElements[selectedAction]), [actionElements, selectedActionTypes]);
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: handleCancel, className: styles.root }, { children: [_jsx(ModalHead, { title: "Update conversations in bulk", onClose: handleCancel }), _jsxs(ModalBody, { children: [_jsx(Text.Paragraph, Object.assign({ withMarginBottom: true }, { children: "Choose which actions you would like to apply to the conversations that you have selected. You can choose one action or add multiple actions to be applied after each other." })), selectedActionElements] }), _jsx(ModalFoot, Object.assign({ cancelName: "Cancel", cancelOnClick: () => handleCancel(), confirmName: `Update (${conversationIds.length})`, confirmOnClick: () => handleConfirm(), confirmProps: { variant: 'primary', disabled: !canPerformBulkUpdate } }, { children: _jsx(DropdownSelect, Object.assign({ containerProps: { className: styles.actionSelectDropdownContainer }, className: styles.actionSelect, items: dropdownSelectActions, itemComponent: SelectItem, noArrow: false, buttonProps: {
                        disabled: !dropdownSelectActions.length || isFetching || !isSuccess,
                    }, disabledItems: disabledActionIds, tooltipItems: dropdownSelectTooltipItems, onSelect: handleActionSelect, itemsDisplayCount: dropdownSelectActions.length }, { children: "Add action" })) }))] })));
});
